import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Checkbox } from '@webapp/common/components/checkbox';
import { useTextStyle } from '@webapp/common/components/custom-styles';
import { AnswerIcon } from '@webapp/common/components/misc';
import { CssPageBlock, CssQuestionType, CssUiComponent } from '@webapp/common/components/survey-custom';
import { Textfield } from '@webapp/common/components/textfield';
import { striptags } from '@webapp/common/lib/utils';
import { AnswerType, CompletePageType, QuestionType } from '@webapp/common/resources/survey';

import type { SurveyQuestion } from '@webapp/survey/src/components/questions/lib';
import type { QuestionAnswerActor } from '@webapp/survey/src/entities/answer-machine';
import { selectInfo, selectIsWithAudioRecogn } from '@webapp/survey/src/machine/selectors';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import { ListSelectResults } from '../test-text';

import css from './list-select.css';

const singleTypes = new Set([QuestionType.ONE_OF_LIST, QuestionType.TEST_ONE_OF_LIST]);

const ListSelectAnswer: VFC<{
    answerActor: QuestionAnswerActor;
    isTestCheckedQuestion: boolean;
    showResults: boolean;
    multiple: boolean;
    disabled: boolean;
}> = ({ answerActor, isTestCheckedQuestion, multiple, showResults, disabled }) => {
    const isWithAudioRecogn = useStateSelector(selectIsWithAudioRecogn);
    const [forwardParentActor, setForwardParentActor] = useState<undefined | QuestionAnswerActor>();
    const forwardParentContext = useSelector(forwardParentActor, (s) => s?.context);

    const forwardParentValue = useSelector(forwardParentContext?.response, (s) => s?.context.extra);
    const answer = useSelector(answerActor, (s) => s?.context);

    const pipingAnswersReplacer = usePipingAnswersReplacer();

    const {
        id,
        response: responseActor,
        testCheckStatus,
        type,
        value: [label],
        forwardParent,
    } = answer;
    const isFpSelf = forwardParentContext?.type === AnswerType.SELF;
    const self = type === AnswerType.SELF;
    const questions = useStateSelector((s) => s.questions);

    useEffect(() => {
        if (!forwardParent && !forwardParentActor) return;

        questions.forEach((q) => {
            const founded = q.getSnapshot().context.answersActors.find((a) => {
                return a.getSnapshot().context.id === forwardParent;
            });
            if (founded) {
                setForwardParentActor(founded);
            }
        });
    }, [questions, forwardParent]);

    const response = useSelector(responseActor, (r) => r);

    const { extra, value: responseValue } = response.context;

    const checked = responseValue as boolean;
    const inputRef = useRef(null);
    const { itemsGap } = useTextStyle();

    const style = useMemo(
        () =>
            self
                ? {
                      rowGap: itemsGap,
                  }
                : {},
        [itemsGap, self],
    );

    const changeSelf = useCallback(
        (value: any): void => {
            responseActor.send({ type: 'CHANGE_EXTRA', value });
        },
        [response],
    );

    const toggle = useCallback(() => {
        const checked = !responseValue;

        if (checked && disabled) return;

        responseActor.send({
            type: 'CHANGE',
            value: checked,
            questionId: response.context.questionId,
            answerId: response.context.answerId,
            groupId: response.context.groupId,
        });

        if (checked && inputRef.current) {
            inputRef.current.focus();
        }
    }, [response, responseValue, disabled]);

    const checkOnInputClick = useCallback(() => {
        responseActor.send({
            type: 'CHANGE',
            value: true,
            questionId: response.context.questionId,
            answerId: response.context.answerId,
            groupId: response.context.groupId,
        });

        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, [response]);

    return useMemo(() => {
        if (answer.hidden || answer.hiddenByAnswersHide) return null;

        return (
            <div className={cn(CssPageBlock.ANSWER, css.answer, self && css.self)} key={id} style={style}>
                <div
                    data-testid={striptags(label)}
                    className={cn(CssUiComponent.CHECKBOX, css.checkbox)}
                    onClick={toggle}
                >
                    {showResults && isTestCheckedQuestion && (
                        <AnswerIcon checked={checked} testCheckStatus={testCheckStatus} />
                    )}
                    <Checkbox
                        checked={checked}
                        label={pipingAnswersReplacer(
                            isFpSelf && forwardParentValue ? forwardParentValue.toString() : label,
                        )}
                        round={!multiple}
                        disabled={!checked && disabled}
                    />
                </div>
                {self && (
                    <Textfield
                        withSpeechRecognition={isWithAudioRecogn}
                        transparent
                        autoFocus={!!responseValue}
                        ref={inputRef}
                        rootClassName={css.text}
                        value={(extra as string) || ''}
                        onChange={changeSelf}
                        onClick={checkOnInputClick}
                    />
                )}
            </div>
        );
    }, [
        isFpSelf,
        forwardParentValue,
        answer.hidden,
        answer.hiddenByAnswersHide,
        self,
        id,
        style,
        toggle,
        showResults,
        isTestCheckedQuestion,
        checked,
        disabled,
        testCheckStatus,
        label,
        multiple,
        responseValue,
        extra,
        changeSelf,
        checkOnInputClick,
        pipingAnswersReplacer,
    ]);
};
ListSelectAnswer.displayName = 'ListSelectAnswer';

export const ListSelect: FC<SurveyQuestion> = ({ question, readOnly }) => {
    const {
        answersActors,
        currentPoints,
        factTime,
        isTestCheckedQuestion,
        maxPoints,
        targetTime,
        type,
        params: { questionColumns, maxLength: max },
        answeredCount,
    } = question;

    const {
        params: {
            main: { completePage },
            tests: { scoreLabel },
        },
    } = useStateSelector(selectInfo);
    const multiple = !singleTypes.has(type);
    const redirect = [CompletePageType.REDIRECT_TO_WEBSITE, CompletePageType.TO_SURVEY].includes(completePage);
    const showResults = !redirect && Boolean(factTime || targetTime || maxPoints || currentPoints) && readOnly;
    const showTimer = Boolean(factTime || targetTime);
    const { itemsGap } = useTextStyle();

    const listStyle = useMemo(
        () => ({
            gap: itemsGap,
            display: questionColumns ? 'block' : undefined,
            columns: questionColumns ? questionColumns : undefined,
        }),
        [itemsGap, questionColumns],
    );

    return (
        <div
            className={cn(
                CssQuestionType.QUESTION,
                CssQuestionType.LIST_SELECT,
                css.list,
                questionColumns && questionColumns !== 1 && css.listWithColumns,
            )}
            style={listStyle}
        >
            {answersActors.map((answer) => {
                return (
                    <ListSelectAnswer
                        answerActor={answer}
                        isTestCheckedQuestion={Boolean(isTestCheckedQuestion)}
                        key={answer.getSnapshot().context.id}
                        multiple={multiple}
                        showResults={showResults}
                        disabled={Boolean(max) && answeredCount >= max}
                    />
                );
            })}
            {showResults && (
                <ListSelectResults
                    answers={answersActors}
                    currentPoints={currentPoints}
                    factTime={factTime}
                    maxPoints={maxPoints}
                    showTimer={showTimer}
                    targetTime={targetTime}
                    pointsLabel={scoreLabel}
                />
            )}
        </div>
    );
};
ListSelect.displayName = 'ListSelect';
